import { photoCountAccusative, photoCountLabel } from '@pz/scene';

/**
 * The photo collage's words ("Kutije sa slikama"), Serbian, informal "ti",
 * calm — docs/photo-collage.md §7. The step (step.ts) and the board
 * (board.ts) both read them from here, so a phrase is changed in one place.
 *
 * Quality is never said in numbers: a photo that would print softer reads the
 * one fixed sentence of photoquality.ts (`PHOTO_TEXTS.warn`), nothing else.
 */

/** Serbian plural classes: 1, 21, 101… / 2–4, 22–24… / everything else (11–14 included). */
export function pluralClass(n: number): 'one' | 'few' | 'many' {
  const whole = Math.trunc(Math.abs(n));
  const tens = whole % 100;
  const ones = whole % 10;
  if (ones === 1 && tens !== 11) return 'one';
  if (ones >= 2 && ones <= 4 && !(tens >= 12 && tens <= 14)) return 'few';
  return 'many';
}

export const COLLAGE_TEXTS = {
  /** The picker button (a question's `config.buttonText` wins). */
  button: 'Izaberi slike',
  /** Under the button while nothing is picked yet. */
  help: (n: number): string =>
    n > 0
      ? `Izaberi ${photoCountAccusative(n)} odjednom — rasporedićemo ih automatski, a posle možeš da menjaš mesta i kadrove.`
      : 'Izaberi sve slike odjednom — rasporedićemo ih automatski, a posle možeš da menjaš mesta i kadrove.',
  tip: 'Savet: u galeriji možeš da označiš više slika odjednom.',
  /** Android on mobile data only. */
  wifi: 'Slanje više slika može da potraje — najbolje preko Wi-Fi mreže.',
  sending: (done: number, total: number): string => `Šaljem slike… ${done} od ${total}`,
  offline: 'Nema interneta. Slanje se nastavlja čim se veza vrati.',
  busy: 'Server obrađuje slike, samo trenutak…',
  resuming: 'Nastavljam slanje slika koje nisu stigle…',
  interrupted:
    'Telefon je osvežio stranicu dok je galerija bila otvorena, pa slike nisu stigle. Tvoji ostali odgovori su sačuvani — izaberi slike ponovo.',
  notSent: (n: number): string => {
    const form = pluralClass(n);
    return form === 'one' ? `${n} slika nije poslata.` : form === 'few' ? `${n} slike nisu poslate.` : `${n} slika nije poslato.`;
  },
  resend: 'Pošalji ponovo',
  unusable: (name: string): string => `„${name}“ ne može da se koristi kao slika.`,
  capped: (max: number, took: number): string =>
    `Na ovaj proizvod staje najviše ${photoCountLabel(max)} (sa rezervom), pa smo uzeli ${
      took === 1 ? 'samo prvu' : pluralClass(took) === 'few' ? `prve ${took}` : `prvih ${took}`
    }.`,
  full: 'Već imaš sve slike koje staju — ukloni neku da dodaš novu.',
  /** "Dalje" and add-to-cart while photos are still on their way. */
  wait: 'Sačekaj da se sve slike pošalju.',
  complete: 'Sve slike su na mestu.',
  missing: (n: number): string => {
    const form = pluralClass(n);
    return form === 'few' ? `Nedostaju još ${photoCountLabel(n)}.` : `Nedostaje još ${photoCountLabel(n)}.`;
  },
  missingHintRepeat: 'Dodaj slike ili dodirni „Ponovi slike“.',
  /** Nothing chosen yet: how many the product wants ("Dodaj 15 slika."). */
  addPhotos: (n: number): string => (n > 0 ? `Dodaj ${photoCountAccusative(n)}.` : 'Dodaj bar jednu sliku.'),
  missingHint: 'Dodaj slike.',
  spares: (n: number): string =>
    pluralClass(n) === 'one'
      ? `U rezervi: ${photoCountLabel(n)}. Ona se ne štampa — u rasporedu je možeš zameniti sa bilo kojom slikom.`
      : `U rezervi: ${photoCountLabel(n)}. One se ne štampaju — u rasporedu ih možeš zameniti sa bilo kojom slikom.`,
  facesHint: 'Na nekim mestima lice možda nije celo. Označili smo ih u rasporedu.',
  repeated: 'Prazna mesta su popunjena ponovljenim slikama. Možeš ih zameniti u rasporedu.',
  relayout: 'Raspored je promenjen — slike koje ne staju su u rezervi.',
  openBoard: 'Uredi raspored',
  shuffle: 'Promešaj',
  shuffled: 'Slike su promešane.',
  undo: 'Poništi',
  addMore: 'Dodaj još slika',
  add: 'Dodaj slike',
  repeat: 'Ponovi slike',
  showAll: (n: number): string => `Prikaži sve (${n})`,
  showLess: 'Prikaži manje',
  /** Tray thumbnails, for a screen reader. */
  trayDone: 'Slika je stigla',
  traySending: 'Slika se šalje',
  trayFailed: 'Slika nije poslata — dodirni da pokušaš ponovo',
  trayUnusable: 'Ova slika ne može da se koristi',
  traySpare: 'U rezervi',
  trayLabel: 'Tvoje slike',
  // ── The board ("Raspored slika") ──────────────────────────────────────────
  title: 'Raspored slika',
  done: 'Gotovo',
  close: 'Zatvori raspored',
  loading: 'Pripremam raspored…',
  hintTouch: 'Dodirni sliku da je urediš. Drži i prevuci da zameniš mesta.',
  hintMouse: 'Klikni sliku da je urediš, ili je prevuci na drugo mesto.',
  hintSelected: 'Dodirni je još jednom za kadriranje. Za drugo mesto: „Zameni mesto“.',
  hintSelectedMouse: 'Klikni je još jednom za kadriranje. Za drugo mesto: „Zameni mesto“.',
  /** After "Zameni mesto": the next tap exchanges. */
  hintSwap: 'Dodirni sliku ili mesto sa kojim menja mesto.',
  hintSwapMouse: 'Klikni sliku ili mesto sa kojim menja mesto.',
  swap: 'Zameni mesto',
  cancelSwap: 'Otkaži',
  undoHint: 'Pogrešno? Dodirni „Poništi“.',
  undoHintMouse: 'Pogrešno? Klikni „Poništi“.',
  hintEmpty: 'Prazno mesto — dodaj sliku ili izaberi iz rezerve.',
  hintSpare: 'Dodirni mesto na proizvodu gde ide ova slika.',
  hintSpareMouse: 'Klikni mesto na proizvodu gde ide ova slika.',
  slot: (i: number, n: number): string => `Mesto ${i} od ${n}`,
  slotEmpty: 'prazno',
  /** The header count, for a screen reader ("24/24" on screen). */
  count: (filled: number, active: number): string => `Popunjeno ${filled} od ${active}`,
  frame: 'Kadriraj',
  auto: 'Automatski',
  replace: 'Nova slika',
  remove: 'Ukloni',
  addHere: 'Dodaj sliku ovde',
  sparesTitle: 'Rezerva',
  spare: (i: number): string => `Slika iz rezerve ${i}`,
  dropSpare: 'Izbaci iz rezerve',
  prev: 'Prethodno mesto',
  next: 'Sledeće mesto',
  zoomIn: 'Uvećaj',
  zoomOut: 'Umanji',
  fit: 'Cela strana',
  badgeEmpty: 'Prazno mesto',
  badgeFace: 'Lice možda nije celo',
  badgeRepeat: 'Ponovljena slika',
  faceLine: 'Lice možda nije celo.',
  /** A hand framing that came from a slot of other proportions. */
  badgeCheck: 'Proveri kadar',
  checkLine: 'Slika je došla sa mesta drugog oblika. Proveri kadar.',
  swapped: 'Slike su zamenile mesta.',
  moved: 'Slika je premeštena.',
  autoReset: 'Vraćen je automatski kadar.',
  undone: 'Poništeno.',
  removed: 'Slika je uklonjena.',
  framerTitle: (i: number): string => `Mesto ${i}`,
  /** A collage over several print sheets: the switcher above the board's stage and the preview's strip. */
  sheets: 'Listovi',
  sheet: (i: number): string => `List ${i}`,
  sheetSlots: (n: number): string => photoCountLabel(n),
  sheetEmpty: (n: number): string => {
    const form = pluralClass(n);
    return form === 'one' ? `${n} prazno mesto` : form === 'few' ? `${n} prazna mesta` : `${n} praznih mesta`;
  },
  framerReset: 'Automatski',
};

/** Longest print-side name shown as it is on a sheet switcher; a longer one reads "List 2". */
export const COLLAGE_SHEET_NAME_MAX = 16;

/**
 * A print sheet's word on a switcher (the board's tabs, the preview's strip):
 * the side's own name when it is short, else "List 2" (1-based `index + 1`).
 */
export function collageSheetLabel(name: string | null | undefined, index: number): string {
  const own = (name ?? '').trim();
  return own && own.length <= COLLAGE_SHEET_NAME_MAX ? own : COLLAGE_TEXTS.sheet(index + 1);
}

/**
 * What "Dalje" and add-to-cart say about a collage with empty active slots:
 * "Nedostaju još 3 slike. Dodaj slike ili dodirni „Ponovi slike“." — without
 * repeats it ends at "Dodaj slike.".
 */
export function collageMissingMessage(missing: number, allowRepeats: boolean): string {
  return `${COLLAGE_TEXTS.missing(missing)} ${allowRepeats ? COLLAGE_TEXTS.missingHintRepeat : COLLAGE_TEXTS.missingHint}`;
}
